/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { GET } from "@/api/get-request";
import { Advisor, Appointment, Time_Schedule, DayInfo } from "@/types/appointment";
import React, {createContext, Dispatch, SetStateAction, useContext, useEffect} from "react";
import { useFormik } from "formik";
import * as yup from 'yup';
import { POST } from "@/api/post-requests";
import dynamic from "next/dynamic";
const Modal = dynamic(() => import('@/components/common/modal'), { ssr: false });
const SuccessDialog = dynamic(() => import('@/components/pages/consultation/success-dialog'), { ssr: false });
import dayjs from 'dayjs';
import moment from 'moment-jalaali';
import { useNotifications } from "@toolpad/core/useNotifications";
import { useAuth } from "./with-auth";
import parsePhoneNumber from 'libphonenumber-js';
import Cookies from 'js-cookie';
import {Rfs__consultation} from "../../_refs/Rfs__consultation";

type FormValues = {
    first_name: string,
    last_name: string,
    mobile: string,
    prefix: string,
    schedule_id: number,
    email: string
};

export type CustomizationProps = {
    GetAdvisors: () => void,
    advisors: Advisor[],
    HandleSelectAdvisor: (advisor: Advisor) => void,
    selected_advisor: Advisor | null,
    formik: ReturnType<typeof useFormik<FormValues>>,
    loading: boolean,
    GetAppointments: () => void,
    appointment: Appointment[],
    handleClose: () => void,
    schedule_times: Time_Schedule[],
    setSelectedDate: Dispatch<SetStateAction<string>>,
    selected_date: string,
    highlightedDays: number[],
    availableDays: number[],
    setCurrentMonth: Dispatch<SetStateAction<string>>,
    setCalenderType: Dispatch<SetStateAction<'gregorian' | 'persian'>>,
    calender_type: 'gregorian' | 'persian',
    phone_codes: { value: string, label: string }[],
    GetDefaultSchedule: (extraQuery?: string) => Promise<void>
};

const AppointmentContext = createContext<CustomizationProps | null>(null);

export const AppointmentProvider = ({ children }: { children: React.ReactNode }) => {
    const [advisors, setAdvisors] = React.useState<Advisor[]>([]);
    const [selected_advisor, setSelectedAdvisor] = React.useState<Advisor | null>(null);
    const [selected_date, setSelectedDate] = React.useState<string>('');
    const [booked_appointment, setBookedAppointment] = React.useState<{
        confirmation: string,
        google_meet_link: string
    } | null>({
        confirmation: "Your free session with Jane Doe is confirmed for April 10, 2025 at 3:00 PM.",
        google_meet_link: "https://meet.google.com/abc-xyz-123"
    });
    const [loading, setLoading] = React.useState<boolean>(false);
    const [appointment, setAppointment] = React.useState<Appointment[]>([]);
    const [schedule_times, setScheduleTimes] = React.useState<Time_Schedule[]>([]);
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => { setOpen(true) };
    const handleClose = () => { setOpen(false) };
    const [highlightedDays, setHighlightedDays] = React.useState<number[]>([]);
    const [availableDays, setAvailableDays] = React.useState<number[]>([]);
    const [current_month, setCurrentMonth] = React.useState<string>('');
    const notifications = useNotifications();
    const [calender_type, setCalenderType] = React.useState<'gregorian' | 'persian'>('gregorian');
    const [phone_codes, setPhoneCodes] = React.useState<{ value: string, label: string }[]>([]);
    const { user } = useAuth();
    const [lang, setLang] = React.useState('en');

    useEffect(()=>{
        const lang = Cookies.get('language') || 'en';
        setLang(lang);
    },[])

    React.useLayoutEffect(() => {

        const GetSelectData = async (url: string) => {
            const response = await GET({ url: `v1/enums/${url}` });
            if (response.success)
                return response.data
            else return []
        };

        const GetData = async () => {

            setPhoneCodes(await GetSelectData('phone_codes'));

        };

        GetData();
    }, []);

    const BookedDays = async () => {
        const isPersian = calender_type === 'persian';
        const displayMonth = isPersian ? moment(current_month, 'YYYY-MM-DD') : dayjs(current_month);

        const groupedByDay: { [key: string]: DayInfo } = schedule_times
            .filter((item: Time_Schedule) => {
                if (isPersian) {
                    const itemJ = moment(item.date as string, 'YYYY-MM-DD');
                    return itemJ.jYear() === displayMonth.jYear() && itemJ.jMonth() === displayMonth.jMonth();
                } else {
                    const itemG = dayjs(item.date);
                    return itemG.year() === (displayMonth as any).year() && itemG.month() === (displayMonth as any).month();
                }
            })
            .reduce((acc: { [key: string]: DayInfo }, item: Time_Schedule) => {
                const dayNumber = isPersian ? moment(item.date as string, 'YYYY-MM-DD').jDate() : dayjs(item.date).date();
                const dateKey = isPersian
                    ? moment(item.date as string, 'YYYY-MM-DD').format('jYYYY-jMM-jDD')
                    : dayjs(item.date).format('YYYY-MM-DD');

                if (!acc[dateKey]) {
                    acc[dateKey] = { day: dayNumber, allBooked: true, hasSessions: false } as any;
                }

                acc[dateKey].hasSessions = true;

                if (!item.is_booked) {
                    (acc[dateKey] as any).allBooked = false;
                }

                return acc;
            }, {});

        const fullyBookedDays: number[] = Object.values(groupedByDay)
            .filter((info: any) => info.hasSessions && info.allBooked)
            .map((info: any) => info.day);

        setHighlightedDays(fullyBookedDays);
    };

    const AvailableDays = async () => {
        const isPersian = calender_type === 'persian';
        const displayMonth = isPersian ? moment(current_month, 'YYYY-MM-DD') : dayjs(current_month);

        const groupedByDay: { [key: string]: DayInfo } = schedule_times
            .filter((item: Time_Schedule) => {
                if (isPersian) {
                    const itemJ = moment(item.date as string, 'YYYY-MM-DD');
                    return itemJ.jYear() === displayMonth.jYear() && itemJ.jMonth() === displayMonth.jMonth();
                } else {
                    const itemG = dayjs(item.date);
                    return itemG.year() === (displayMonth as any).year() && itemG.month() === (displayMonth as any).month();
                }
            })
            .reduce((acc: { [key: string]: DayInfo }, item: Time_Schedule) => {
                const dayNumber = isPersian ? moment(item.date as string, 'YYYY-MM-DD').jDate() : dayjs(item.date).date();
                const dateKey = isPersian
                    ? moment(item.date as string, 'YYYY-MM-DD').format('jYYYY-jMM-jDD')
                    : dayjs(item.date).format('YYYY-MM-DD');

                if (!acc[dateKey]) {
                    acc[dateKey] = { day: dayNumber, hasUnbooked: false, hasSessions: false } as any;
                }

                acc[dateKey].hasSessions = true;

                if (!item.is_booked) {
                    (acc[dateKey] as any).hasUnbooked = true;
                }

                return acc;
            }, {});

        const availableDays: number[] = Object.values(groupedByDay)
            .filter((info: any) => info.hasSessions && info.hasUnbooked)
            .map((info: any) => info.day);

        setAvailableDays(availableDays);
    };

    React.useEffect(() => {
        BookedDays();
        AvailableDays();
    }, [current_month, schedule_times]);


    React.useEffect(() => {
        setSelectedDate('');
    }, [selected_advisor]);

    const GetAppointments = async () => {
        setLoading(true);
        const response = await GET({ url: `v1/timeschedule/my-appointments?timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}` });
        if (response.success)
            setAppointment(response.data);
        setLoading(false);
    };

    const GetAdvisors = async () => {
        const response = await GET({
            url: 'v1/timeschedule/advisors'
        });
        if (response.success)
            setAdvisors(response.data);
    };

    const GetDefaultSchedule = async (extraQuery: string = '') => {
        const timezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
        const url = `v1/timeschedule/schedule?timezone=${timezone}${extraQuery}`;
        const response = await GET({ url });
        if (response.success) {
            setScheduleTimes(response.data);
            // Select default advisor from first schedule item if present
            setSelectedAdvisor(response.data?.[0]?.advisor ?? null);
            setCurrentMonth(`${dayjs().format('YYYY-MM-DD')}`);
            await BookedDays();
            await AvailableDays();
        } else {
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });
        }
    };

    const validationSchema = yup.object({
        first_name: yup
            .string()
            .required('First name is required'),
        last_name: yup
            .string()
            .required('Last name is required'),
        mobile: yup
            .string()
            .required('Phone number is required'),
        email: yup
            .string()
            .email()
            .required('Email is required'),
    });


    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: {
            first_name: user?.first_name || "",
            last_name: user?.last_name || "",
            mobile: parsePhoneNumber(user?.mobile_number || '')?.nationalNumber || "",
            prefix: parsePhoneNumber(user?.mobile_number || '')?.country || ((Cookies.get('language') || 'en') === 'fa' ? 'IR' : 'CA'),
            schedule_id: -1,
            email: user?.email || ""
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);

            const response = await POST({
                url: 'v1/timeschedule/book',
                body: {
                    first_name: values.first_name,
                    last_name: values.last_name,
                    mobile: '+' + phone_codes.find(item => item.label === values.prefix)?.value + values.mobile,
                    schedule_id: values.schedule_id,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    email: values.email,
                    lang:lang
                }
            });
            if (response.success) {
                setBookedAppointment({
                    confirmation: response.data.confirmation,
                    google_meet_link: response.data.google_meet_link
                });
                handleOpen();
            } else
                Object.keys(response.errors).forEach((key) => notifications.show(response.errors[key], {
                    severity: 'error',
                    autoHideDuration: 3000,
                }))

            setLoading(false)
        },
    });


    const HandleSelectAdvisor = async (advisor: Advisor) => {
        setSelectedAdvisor(advisor);

        const response = await GET({
            url: `v1/timeschedule/schedule?advisor_id=${advisor.id}&timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
        });
        if (response.success) {
            setScheduleTimes(response.data);
            await BookedDays();
            await AvailableDays();
        }
        else
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });

        setCurrentMonth(`${dayjs().format('YYYY-MM-DD')}`);
    };

    Rfs__consultation.appointment_provider();

    return <AppointmentContext.Provider
        value={{
            GetAdvisors,
            advisors,
            HandleSelectAdvisor,
            selected_advisor,
            formik,
            loading,
            GetAppointments,
            appointment,
            handleClose,
            schedule_times,
            selected_date,
            setSelectedDate,
            highlightedDays,
            availableDays,
            setCurrentMonth,
            calender_type,
            setCalenderType,
            phone_codes,
            GetDefaultSchedule
        }}
    >
        {children}

        <Modal
            open={open}
            handleClose={handleClose}
        >
            {booked_appointment ?
                <SuccessDialog
                    appointment={booked_appointment}
                />
                :
                <></>
            }
        </Modal>

    </AppointmentContext.Provider>
};


export const useAppointment = () => {
    const context = useContext(AppointmentContext);
    if (!context) {
        throw new Error("useAppointment must be used within an AppointmentProvider");
    }
    return context;
};
